<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { extractMediaBankEntryIdsFromHtml } from './media-lookup';
import { useAppStateStore } from '@/stores/AppStateStore.js'
const appStateStore = useAppStateStore();

const props = defineProps({
    containerRef: { type: String, required: true },
    contentId: { type: String, required: true },
    pageTitle: { type: String, required: true },
    containerType: { type: String, required: true },
    exitUrl: { type: String, required: true },
    generateUrl: { type: String, required: true },
    saveUrl: { type: String, required: true },
    conversationUrl: { type: String, required: true },
    updateHtmlUrl: { type: String, required: true },
    pollUrl: { type: String, required: true },
    previewUrl: { type: String, required: true },
    mediaBankUrl: { type: String, default: '' },
    resolveMediaUrl: { type: String, default: '' },
    contextUrl: { type: String, default: '' },
    designReferencesUrl: { type: String, default: '' },
    checkWebsiteMigrationUrl: { type: String, default: '' },
    fetchWebsiteUrl: { type: String, default: '' },
    flowieHandoff: { type: Object, default: null },
});

const componentLibraryRef = ref(null);
const pendingAttachments = ref([]);
const onboardingRef = ref(null);
const previewRef = ref(null);
const currentHtml = ref('');
const chatMessages = ref([]);
const isGenerating = ref(false);
const isSaving = ref(false);
const savingState = ref('');
const saveRequest = ref(null);
const deviceMode = ref('desktop');
const undoStack = ref([]);
const redoStack = ref([]);
const pollInterval = ref(null);
const generateError = ref(null);
const generatingStep = ref('');

// Context / onboarding state
const pageContext = ref(null);
const showContextPanel = ref(false);

// Website migration state
const showMigrationPrompt = ref(false);
const migrationWebsiteUrl = ref('');
const migrationBusinessName = ref('');
const migrationDismissed = ref(false);

const showOnboarding = computed(() =>
    chatMessages.value.length === 0 && !currentHtml.value && !pageContext.value && !isGenerating.value && !showMigrationPrompt.value
);

// Image picker state
const imagePickerVisible = ref(false);
const editingImage = ref(null);

// Media URL resolution
const mediaUrlMap = ref({});
const mediaEntryMap = ref({});

const deviceWidth = computed(() => {
    switch (deviceMode.value) {
        case 'tablet': return '768px';
        case 'phone': return '375px';
        default: return '100%';
    }
});

const canUndo = computed(() => undoStack.value.length > 0);
const canRedo = computed(() => redoStack.value.length > 0);
const isPageContextReadOnly = computed(() => Boolean(currentHtml.value?.trim()));
const isContentInteractionLocked = computed(() => isGenerating.value);
const isPreviewRefreshing = computed(() => {
    const refreshing = previewRef.value?.isIframeRefreshing;

    if (typeof refreshing === 'object' && refreshing !== null && 'value' in refreshing) {
        return refreshing.value;
    }

    return Boolean(refreshing);
});

const pushUndoState = () => {
    undoStack.value.push(currentHtml.value);
    if (undoStack.value.length > 50) {
        undoStack.value.shift();
    }
    redoStack.value = [];
};

const undo = () => {
    if (isContentInteractionLocked.value) return;
    if (!canUndo.value) return;
    redoStack.value.push(currentHtml.value);
    currentHtml.value = undoStack.value.pop();
};

const redo = () => {
    if (isContentInteractionLocked.value) return;
    if (!canRedo.value) return;
    undoStack.value.push(currentHtml.value);
    currentHtml.value = redoStack.value.pop();
};

const refreshPreview = () => {
    if (isPreviewRefreshing.value || isGenerating.value) return;
    previewRef.value?.refreshIframe?.();
};

const handleKeydown = (e) => {
    if (isContentInteractionLocked.value) return;

    if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
            redo();
        } else {
            undo();
        }
    }
};

const loadConversation = async () => {
    try {
        const res = await axios.get(props.conversationUrl);
        chatMessages.value = res.data.messages || [];
        if (res.data.html) {
            currentHtml.value = res.data.html;
        }
        if (res.data.context) {
            pageContext.value = res.data.context;
        }
    } catch (e) {
        // Conversation might not exist yet
    }
};

const stopPolling = () => {
    if (pollInterval.value) {
        clearTimeout(pollInterval.value);
        pollInterval.value = null;
    }
};

// Paige's reply when there is no AI usage left. `localOnly` keeps it out of the
// history sent with the next prompt; the server never stores it either.
const pushBudgetExceededNotice = () => {
    chatMessages.value.push({
        role: 'assistant',
        notice: _mft('page:aiPageBuilder.error.budgetExceeded'),
        budgetExceeded: true,
        localOnly: true,
    });
};

const startPolling = () => {
    let retries = 0;
    const maxRetries = 100;
    let pollDelay = 1000; // Start fast (1s)

    const schedulePoll = () => {
        pollInterval.value = setTimeout(async () => {
            retries++;

            if (retries > maxRetries) {
                isGenerating.value = false;
                generateError.value = _mft('page:aiPageBuilder.error.timeout');
                return;
            }

            // Adaptive: 1s for first 10, then 3s
            if (retries > 10) pollDelay = 3000;

            try {
                const res = await axios.get(props.pollUrl);
                const data = res.data;

                if (data.status === 'processing' && data.step) {
                    generatingStep.value = data.step;
                }

                if (data.status === 'completed') {
                    if (data.html) {
                        currentHtml.value = data.html;
                        chatMessages.value.push({
                            role: 'assistant',
                            content: data.html,
                            summary: data.summary || null,
                        });
                    }
                    isGenerating.value = false;
                    generatingStep.value = '';
                    return; // Stop polling
                } else if (data.status === 'budget_exceeded') {
                    isGenerating.value = false;
                    generatingStep.value = '';
                    pushBudgetExceededNotice();
                    return; // Stop polling
                } else if (data.status === 'failed') {
                    isGenerating.value = false;
                    generateError.value = _mft('page:aiPageBuilder.error.failed');
                    console.warn(data.error || 'Generation failed without error message');
                    return; // Stop polling
                }
            } catch (e) {
                // Network error during poll — keep retrying
            }
            schedulePoll();
        }, pollDelay);
    };
    schedulePoll();
};

const handleChatSend = (prompt) => {
    const attachments = pendingAttachments.value.map((attachment) => ({
        type: attachment.type,
        config: JSON.parse(JSON.stringify(attachment.config ?? {})),
    }));
    pendingAttachments.value = [];
    handleGenerate(prompt, { attachments });
};

const handleConfigureAttachment = ({ type, config = {}, index = null }) => {
    componentLibraryRef.value?.configureAttachment(type, config, index);
};

const handleAttachmentConfigured = ({ type, config, index }) => {
    const attachment = { type, config };

    if (index === null) {
        pendingAttachments.value.push(attachment);
        return;
    }

    pendingAttachments.value.splice(index, 1, attachment);
};

const handleRemoveAttachment = (index) => {
    pendingAttachments.value.splice(index, 1);
};

const handleGenerate = async (prompt, { attachments = [], websiteMigrationUrl = null } = {}) => {
    if (isGenerating.value) return;

    pushUndoState();
    isGenerating.value = true;
    generateError.value = null;
    generatingStep.value = '';

    const userMessage = { role: 'user', content: prompt, attachments };
    chatMessages.value.push(userMessage);

    const historyForApi = chatMessages.value
        .filter(m => (m.role === 'user' || m.role === 'assistant') && !m.localOnly)
        .slice(-10);

    // Remove the last user message from history (it's the current prompt)
    historyForApi.pop();

    try {
        const payload = {
            prompt: prompt,
            history: historyForApi,
        };
        if (attachments.length) {
            payload.attachments = attachments.map(a => ({
                type: a.type,
                config: a.config ?? {},
            }));
        }
        if (websiteMigrationUrl) {
            payload.website_migration_url = websiteMigrationUrl;
        }
        const { data } = await axios.post(props.generateUrl, payload);

        if (data?.status === 'budget_exceeded') {
            isGenerating.value = false;
            // The server did not store this prompt, so it must not be sent back
            // as history either.
            userMessage.localOnly = true;
            pushBudgetExceededNotice();
            return;
        }

        startPolling();
    } catch (e) {
        isGenerating.value = false;
        generateError.value = _mft('page:aiPageBuilder.error.startFailed');
    }
};

const saveCurrentHtml = async () => {
    if (saveRequest.value) {
        return saveRequest.value;
    }

    isSaving.value = true;
    savingState.value = 'waiting';

    saveRequest.value = axios.post(props.saveUrl, { html: currentHtml.value })
        .then(() => {
            savingState.value = 'success';
            setTimeout(() => { savingState.value = ''; }, 2000);

            return true;
        })
        .catch(() => {
            savingState.value = 'error';

            return false;
        })
        .finally(() => {
            isSaving.value = false;
            saveRequest.value = null;
        });

    return saveRequest.value;
};

const handleSave = async () => {
    await saveCurrentHtml();
};

const handlePreviewInNewTab = async () => {
    if (isGenerating.value || typeof window === 'undefined') return;

    // Open a placeholder tab synchronously to avoid popup blockers,
    // then only navigate to the preview after the save succeeds.
    const previewWindow = window.open('about:blank', '_blank');
    const saved = await saveCurrentHtml();

    if (!saved) {
        previewWindow?.close?.();

        return;
    }

    if (previewWindow) {
        previewWindow.opener = null;
        previewWindow.location = props.previewUrl;

        return;
    }

    window.open(props.previewUrl, '_blank');
};

const handleHtmlUpdate = (newHtml) => {
    if (isContentInteractionLocked.value) return;
    pushUndoState();
    currentHtml.value = newHtml;
    // Persist inline edit silently
    axios.post(props.updateHtmlUrl, { html: newHtml }).catch(() => {});
};

const handleComponentInsert = (componentHtml) => {
    if (isContentInteractionLocked.value) return;
    pushUndoState();
    currentHtml.value += '\n' + componentHtml;
    axios.post(props.updateHtmlUrl, { html: currentHtml.value }).catch(() => {});
};

const handleComponentEdit = ({ componentType, config, index }) => {
    if (isContentInteractionLocked.value) return;
    if (componentLibraryRef.value) {
        componentLibraryRef.value.editComponent(componentType, config, index);
    }
};

const handleComponentUpdate = ({ type, config, index }) => {
    if (isContentInteractionLocked.value) return;
    pushUndoState();
    // Replace the config attribute on the Nth mf-component in the HTML
    let count = 0;
    const updated = currentHtml.value.replace(
        /<mf-component\s[^>]*>/g,
        (match) => {
            if (count++ === index) {
                return `<mf-component type="${type}" config='${config}'>`;
            }
            return match;
        }
    );
    currentHtml.value = updated;
    axios.post(props.updateHtmlUrl, { html: currentHtml.value }).catch(() => {});
};

const handleComponentDelete = ({ index }) => {
    if (isContentInteractionLocked.value) return;
    if (typeof document === 'undefined') return;

    const container = document.createElement('div');
    container.innerHTML = currentHtml.value || '';
    const component = container.querySelectorAll('mf-component')[index];

    if (!component) return;

    pushUndoState();
    component.remove();
    currentHtml.value = container.innerHTML;
    axios.post(props.updateHtmlUrl, { html: currentHtml.value }).catch(() => {});
};

// Image picker handlers
const handleImageEdit = (info) => {
    if (isContentInteractionLocked.value) return;
    editingImage.value = info;
    imagePickerVisible.value = true;
};

const handleImageSelect = ({ src, alt, mediaId }) => {
    if (isContentInteractionLocked.value || editingImage.value === null) return;

    pushUndoState();

    const targetIndex = editingImage.value.index;
    let imgCount = 0;

    const updated = currentHtml.value.replace(
        /<img([^>]*?)>/g,
        (match, attrs) => {
            if (imgCount++ !== targetIndex) return match;

            // Preserve existing classes
            const classMatch = attrs.match(/class="([^"]*)"/);
            const existingClasses = classMatch ? classMatch[1] : '';

            // Build new attributes
            let newAttrs = '';
            if (existingClasses) {
                newAttrs += ` class="${existingClasses}"`;
            }
            newAttrs += ` alt="${alt.replace(/"/g, '&quot;')}"`;

            if (mediaId) {
                newAttrs += ` data-media-id="${mediaId}" src=""`;
            } else {
                newAttrs += ` src="${src.replace(/"/g, '&quot;')}"`;
            }

            return `<img${newAttrs}>`;
        }
    );

    currentHtml.value = updated;
    axios.post(props.updateHtmlUrl, { html: currentHtml.value }).catch(() => {});

    imagePickerVisible.value = false;
    editingImage.value = null;

    // Trigger media resolution if needed
    if (mediaId) {
        resolveMediaIds();
    }
};

// Media URL resolution
const resolveMediaIds = async () => {
    if (!props.resolveMediaUrl) return;

    const ids = extractMediaBankEntryIdsFromHtml(currentHtml.value);
    if (ids.length === 0) {
        mediaUrlMap.value = {};
        mediaEntryMap.value = {};
        return;
    }

    // Only resolve IDs we don't already have
    const missingIds = ids.filter(id => !mediaEntryMap.value[id] && !mediaUrlMap.value[id]);
    if (missingIds.length === 0) return;

    try {
        const res = await axios.post(props.resolveMediaUrl, {
            entry_ids: missingIds.map(id => parseInt(id)),
        });
        mediaUrlMap.value = { ...mediaUrlMap.value, ...(res.data.urls || {}) };
        mediaEntryMap.value = { ...mediaEntryMap.value, ...(res.data.entries || {}) };
    } catch (e) {
        // Silently fail
    }
};

watch(() => currentHtml.value, () => {
    resolveMediaIds();
});

watch(isContentInteractionLocked, (isLocked) => {
    if (!isLocked) return;

    imagePickerVisible.value = false;
    editingImage.value = null;
});

const checkWebsiteMigration = async () => {
    if (!props.checkWebsiteMigrationUrl) return;

    try {
        const res = await axios.get(props.checkWebsiteMigrationUrl);
        if (res.data.should_migrate) {
            migrationWebsiteUrl.value = res.data.website_url;
            migrationBusinessName.value = res.data.business_name || '';
            showMigrationPrompt.value = true;
        }
    } catch (e) {
        // Silently fail — just show normal onboarding
    }
};

const handleMigrationAccept = async () => {
    showMigrationPrompt.value = false;

    // Mark as migrated on the backend
    try {
        await axios.post(props.fetchWebsiteUrl, {
            url: migrationWebsiteUrl.value,
        });
    } catch (e) {
        // Non-blocking — the AI will still fetch the URL itself
    }

    const prompt = `Import all the content from my website at ${migrationWebsiteUrl.value} and build a beautiful new page with it.`;
    handleGenerate(prompt, { websiteMigrationUrl: migrationWebsiteUrl.value });
};

const handleMigrationDecline = () => {
    showMigrationPrompt.value = false;
    migrationDismissed.value = true;
};

const handleOnboardingComplete = async (context) => {
    try {
        await axios.post(props.contextUrl, context);
        pageContext.value = context;

        // Auto-compose a natural first prompt from the context fields
        let prompt = context.topic;
        if (context.audience) {
            prompt += `\n\nThe target audience is ${context.audience}.`;
        }
        if (context.cta) {
            prompt += ` The main call to action should be: ${context.cta}.`;
        }
        if (context.language) {
            prompt += `\n\nWrite all content in ${context.language}.`;
        }

        handleGenerate(prompt);
    } catch (e) {
        if (onboardingRef.value) {
            onboardingRef.value.resetLoading();
        }
    }
};

const handleContextUpdate = (context) => {
    pageContext.value = context;
    showContextPanel.value = false;
};

const dismissError = () => {
    generateError.value = null;
};

onMounted(async () => {
    await loadConversation();
    document.addEventListener('keydown', handleKeydown);

    appStateStore.setBottomRightToolbarMinimized(true)

    // Only check migration if this is a blank page (no content yet)
    if (!currentHtml.value && chatMessages.value.length === 0 && !pageContext.value) {
        await checkWebsiteMigration();
    }
});

onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown);
    stopPolling();
});
</script>

<template>
    <div class="flex flex-col h-dvh">
        <!-- Toolbar -->
        <frosted-bar :teleport="false">
            <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                    <a :href="exitUrl">
                        <button-secondary>
                            <mf-translate domainkey="page:pagebuilder.exit"></mf-translate>
                        </button-secondary>
                    </a>
                    <span class="text-sm font-medium text-gray-700 truncate max-w-xs">{{ pageTitle }}</span>
                </div>

                <div class="flex items-center space-x-2">
                    <button-secondary
                        size="sm"
                        :disabled="isPreviewRefreshing || isGenerating"
                        class="!shadow-none"
                        :title="isPreviewRefreshing ? 'Refreshing preview' : 'Refresh preview'"
                        @click="refreshPreview"
                    >
                        <i class="fa-regular fa-arrows-rotate"></i>
                    </button-secondary>

                    <div v-if="false">
                        <!-- Undo/Redo -->
                        <!-- Disable Undo/Redo for now -->
                        <button-base
                            @click="undo"
                            style-type="default"
                            :disabled="!canUndo || isContentInteractionLocked"
                            size="xs"
                            class="!border-0 !bg-transparent !px-2 !py-2"
                            title="Undo (Ctrl+Z)"
                        >
                            <template #icon>
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a5 5 0 015 5v2M3 10l4-4m-4 4l4 4"/>
                                </svg>
                            </template>
                        </button-base>
                        <button-base
                            @click="redo"
                            style-type="default"
                            :disabled="!canRedo || isContentInteractionLocked"
                            size="xs"
                            class="!border-0 !bg-transparent !px-2 !py-2"
                            title="Redo (Ctrl+Shift+Z)"
                        >
                            <template #icon>
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 10H11a5 5 0 00-5 5v2m15-7l-4-4m4 4l-4 4"/>
                                </svg>
                            </template>
                        </button-base>
                    </div>

                    <!-- Device toggle -->
                    <div class="flex items-center border rounded overflow-hidden ml-2">
                        <button-base
                            @click="deviceMode = 'desktop'"
                            :style-type="deviceMode === 'desktop' ? 'tinted' : 'default'"
                            size="sm"
                            class="!rounded-none !border-0 !ring-0"
                            title="Desktop"
                        >
                            <template #icon>
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                            </template>
                        </button-base>
                        <button-base
                            @click="deviceMode = 'tablet'"
                            :style-type="deviceMode === 'tablet' ? 'tinted' : 'default'"
                            size="sm"
                            class="!rounded-none !border-0 !ring-0"
                            title="Tablet"
                        >
                            <template #icon>
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>
                            </template>
                        </button-base>
                        <button-base
                            @click="deviceMode = 'phone'"
                            :style-type="deviceMode === 'phone' ? 'tinted' : 'default'"
                            size="sm"
                            class="!rounded-none !border-0 !ring-0"
                            title="Phone"
                        >
                            <template #icon>
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>
                            </template>
                        </button-base>
                    </div>

                    <!-- Context settings -->
                    <button-base
                        v-if="containerType !== 'expert-contract'"
                        @click="showContextPanel = !showContextPanel"
                        style-type="default"
                        :class="{ '!bg-gray-200': showContextPanel }"
                        size="xs"
                        class="ml-2 !border-0 !bg-transparent !px-2 !py-2"
                        :title="_mft('page:aiPageBuilder.context.settings')"
                    >
                        <template #icon>
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                            </svg>
                        </template>
                    </button-base>

                    <!-- Save -->
                    <button-base
                        style-type="default"
                        @click="handlePreviewInNewTab"
                        :disabled="isSaving || isGenerating"
                        class="ml-2"
                    >
                        <i class="fa-regular fa-arrow-up-right-from-square mr-2"></i>
                        <mf-translate domainkey="page:pagebuilder.preview"></mf-translate>
                    </button-base>

                    <button-base
                        style-type="success"
                        @click="handleSave"
                        :state="savingState"
                        class="ml-2"
                    >
                        <mf-translate domainkey="page:pagebuilder.save"></mf-translate>
                    </button-base>
                </div>
            </div>
        </frosted-bar>

        <!-- Error banner -->
        <div v-if="generateError" class="px-4 py-2 bg-red-50 border-b border-red-200 flex items-center justify-between">
            <span class="text-sm text-red-700">{{ generateError }}</span>
            <button-default @click="dismissError" size="xs" class="ml-4 !border-0 !bg-transparent !text-red-500"><mf-translate domainkey="page:aiPageBuilder.dismiss"></mf-translate></button-default>
        </div>

        <!-- Main content area -->
        <div class="flex flex-1 overflow-hidden">
            <!-- Preview pane -->
            <div class="relative flex-1 overflow-auto bg-gray-100 p-4 flex items-start justify-center h-full">
                <!-- Skeleton loading state -->
                <div v-if="isGenerating && !currentHtml" class="w-full max-w-4xl animate-pulse space-y-4 p-6">
                    <!-- Hero skeleton -->
                    <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                        <div class="h-8 bg-gray-200 rounded w-2/3 mx-auto mb-4"></div>
                        <div class="h-4 bg-gray-200 rounded w-1/2 mx-auto mb-6"></div>
                        <div class="h-64 bg-gray-100 rounded-lg mb-4"></div>
                    </div>
                    <!-- Content block skeletons -->
                    <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                        <div class="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                        <div class="grid grid-cols-3 gap-4">
                            <div v-for="i in 3" :key="i" class="space-y-3">
                                <div class="h-32 bg-gray-100 rounded"></div>
                                <div class="h-4 bg-gray-200 rounded w-3/4"></div>
                                <div class="h-3 bg-gray-200 rounded w-full"></div>
                                <div class="h-3 bg-gray-200 rounded w-5/6"></div>
                            </div>
                        </div>
                    </div>
                    <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                        <div class="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
                        <div class="space-y-2">
                            <div class="h-3 bg-gray-200 rounded w-full"></div>
                            <div class="h-3 bg-gray-200 rounded w-full"></div>
                            <div class="h-3 bg-gray-200 rounded w-3/4"></div>
                        </div>
                    </div>
                    <p class="text-xs text-gray-400 text-center mt-6">
                        <span v-if="generatingStep === 'researching'">
                            <mf-translate domainkey="page:aiPageBuilder.generatingResearching"></mf-translate>
                        </span>
                        <span v-else-if="generatingStep === 'designing'">
                            <mf-translate domainkey="page:aiPageBuilder.generatingDesigning"></mf-translate>
                        </span>
                        <span v-else-if="generatingStep === 'building'">
                            <mf-translate domainkey="page:aiPageBuilder.generatingBuilding"></mf-translate>
                        </span>
                        <span v-else>
                            <mf-translate domainkey="page:aiPageBuilder.generating"></mf-translate>
                        </span>
                    </p>
                </div>

                <div
                    v-else
                    :style="{ width: deviceWidth, maxWidth: '100%' }"
                    class="transition-all duration-300 h-full"
                >
                    <ai-page-builder-preview
                        ref="previewRef"
                        :html="currentHtml"
                        :device-mode="deviceMode"
                        :media-url-map="mediaUrlMap"
                        :media-entry-map="mediaEntryMap"
                        :interaction-locked="isContentInteractionLocked"
                        :container-type="containerType"
                        :container-ref="containerRef"
                        @update-html="handleHtmlUpdate"
                        @edit-component="handleComponentEdit"
                        @edit-image="handleImageEdit"
                    ></ai-page-builder-preview>
                </div>

                <div
                    v-if="isContentInteractionLocked && currentHtml"
                    class="absolute inset-0 z-10 cursor-progress bg-white/40"
                    aria-hidden="true"
                ></div>
            </div>

            <!-- Sidebar: Migration / Onboarding / Chat / Context + Components -->
            <div class="w-[400px] flex-shrink-0 border-l border-gray-200 bg-white flex flex-col min-h-0 overflow-y-auto max-h-screen">
                <!-- Website migration prompt -->
                <div v-if="showMigrationPrompt" class="flex-1 min-h-0 flex flex-col">
                    <div class="flex-1 overflow-y-auto p-5">
                        <div class="text-center py-8">
                            <div class="text-5xl mb-6">🌐</div>
                            <h2 class="text-lg font-semibold text-gray-900 mb-3">
                                <mf-translate domainkey="page:aiPageBuilder.migration.title"></mf-translate>
                            </h2>
                            <p class="text-sm text-gray-500 mb-5">
                                <mf-translate domainkey="page:aiPageBuilder.migration.description"></mf-translate>
                            </p>
                            <div class="bg-gray-50 rounded-lg p-3 mb-6">
                                <span class="text-xs text-gray-400 block mb-1">
                                    <mf-translate domainkey="page:aiPageBuilder.migration.websiteLabel"></mf-translate>
                                </span>
                                <a :href="migrationWebsiteUrl" target="_blank" class="mf-link text-sm font-medium break-all">
                                    {{ migrationWebsiteUrl }}
                                </a>
                            </div>
                        </div>
                    </div>
                    <div class="flex-shrink-0 p-5 pt-3 border-t border-gray-200 space-y-2">
                        <button-primary
                            @click="handleMigrationAccept"
                            size="lg"
                            class="w-full justify-center"
                        >
                            <mf-translate domainkey="page:aiPageBuilder.migration.yes"></mf-translate>
                        </button-primary>
                        <button-secondary
                            @click="handleMigrationDecline"
                            size="lg"
                            class="w-full justify-center"
                        >
                            <mf-translate domainkey="page:aiPageBuilder.migration.no"></mf-translate>
                        </button-secondary>
                    </div>
                </div>

                <!-- Onboarding (new page, no content) -->
                <ai-page-builder-onboarding
                    v-else-if="showOnboarding"
                    ref="onboardingRef"
                    class="flex-1 min-h-0"
                    :design-references-url="designReferencesUrl"
                    :flowie-handoff="flowieHandoff"
                    @complete="handleOnboardingComplete"
                ></ai-page-builder-onboarding>

                <!-- Context settings panel -->
                <ai-page-builder-context-panel
                    v-else-if="showContextPanel"
                    class="flex-1 min-h-0"
                    :context="pageContext"
                    :context-url="contextUrl"
                    :design-references-url="designReferencesUrl"
                    :read-only="isPageContextReadOnly"
                    @update="handleContextUpdate"
                    @close="showContextPanel = false"
                ></ai-page-builder-context-panel>

                <!-- Chat -->
                <ai-page-builder-chat
                    v-else
                    class="flex-1 min-h-0"
                    :messages="chatMessages"
                    :is-generating="isGenerating"
                    :pending-attachments="pendingAttachments"
                    @send="handleChatSend"
                    @configure-attachment="handleConfigureAttachment"
                    @remove-attachment="handleRemoveAttachment"
                ></ai-page-builder-chat>

                <div
                    class="flex-shrink-0"
                    :class="{ 'pointer-events-none opacity-60': isContentInteractionLocked }"
                >
                    <ai-page-builder-component-library
                        ref="componentLibraryRef"
                        @insert="handleComponentInsert"
                        @update="handleComponentUpdate"
                        @remove="handleComponentDelete"
                        @attachment-configured="handleAttachmentConfigured"
                    ></ai-page-builder-component-library>
                </div>
            </div>
        </div>

        <!-- Image Picker Modal -->
        <ai-page-builder-image-picker
            :visible="imagePickerVisible"
            :current-src="editingImage?.currentSrc || ''"
            :current-alt="editingImage?.currentAlt || ''"
            :media-id="editingImage?.mediaId || null"
            :media-bank-url="mediaBankUrl"
            @select="handleImageSelect"
            @close="imagePickerVisible = false"
        ></ai-page-builder-image-picker>
    </div>
</template>
